import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  VideoOff, Play, QrCode, MessageCircle, Copy, Check, Instagram, Facebook, Youtube, Music, ExternalLink, HandHeart,
} from "lucide-react";
import { toast } from "sonner";
import { getVideo, getContact, trackScan, trackPlay, trackAmen } from "../lib/api";
import { resolveVideo, getPoster } from "../lib/video";
import { TikTokIcon } from "./TikTokIcon";

export const PublicVideoView = () => {
  const { id } = useParams();
  const qrId = id || "main";
  const [video, setVideo] = useState(null);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [started, setStarted] = useState(false);
  const [pixCopied, setPixCopied] = useState(false);
  const [amenDone, setAmenDone] = useState(false);
  const [amenLoading, setAmenLoading] = useState(false);

  const amenKey = `amen-${qrId}-${new Date().toISOString().slice(0, 10)}`;

  useEffect(() => {
    setAmenDone(!!localStorage.getItem(amenKey));
  }, [amenKey]);

  const handleAmen = async () => {
    if (amenDone || amenLoading) return;
    try {
      setAmenLoading(true);
      await trackAmen(qrId);
      localStorage.setItem(amenKey, "1");
      setAmenDone(true);
      toast.success("Amém! 🙏 Que Deus abençoe você.");
    } catch {
      toast.error("Não foi possível registrar. Tente novamente.");
    } finally {
      setAmenLoading(false);
    }
  };

  useEffect(() => {
    trackScan(qrId);
    Promise.all([
      getVideo(qrId).then(setVideo).catch(() => setVideo({ published: false })),
      getContact(qrId).then(setContact).catch(() => setContact({})),
    ]).finally(() => setLoading(false));
  }, [qrId]);

  const resolved = video?.published ? resolveVideo(video.source_type, video.url) : { kind: "none" };
  const poster = video?.published ? getPoster(video) : null;
  const mt = video?.media_type || "video";

  const handleStart = () => {
    if (resolved.kind === "link") {
      trackPlay(qrId);
      window.open(resolved.src, "_blank", "noopener");
      return;
    }
    trackPlay(qrId);
    setStarted(true);
  };

  const autoplaySrc = () => {
    const sep = resolved.src.includes("?") ? "&" : "?";
    return `${resolved.src}${sep}autoplay=1`;
  };

  const copyPix = async () => {
    try {
      await navigator.clipboard.writeText(contact.pix_key);
      setPixCopied(true);
      toast.success("Chave Pix copiada!");
      setTimeout(() => setPixCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar a chave.");
    }
  };

  const c = contact || {};
  const hasCards =
    (c.whatsapp_enabled && c.whatsapp_number) ||
    (c.pix_enabled && c.pix_key) ||
    (c.instagram_enabled && c.instagram_url) ||
    (c.facebook_enabled && c.facebook_url) ||
    (c.youtube_enabled && c.youtube_url) ||
    (c.tiktok_enabled && c.tiktok_url);

  return (
    <div
      data-testid="public-video-page-container"
      className="min-h-[calc(100vh-73px)] bg-gradient-to-b from-background to-secondary/40"
    >
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-16">
        {loading ? (
          <div className="aspect-video w-full animate-pulse rounded-2xl bg-secondary" />
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {video?.published ? (
              <>
                {mt === "image" ? (
                  <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
                    <img src={video.url} alt={video.title || "Imagem"} data-testid="public-image" className="mx-auto max-h-[70vh] w-full object-contain" />
                  </div>
                ) : mt === "audio" ? (
                  <div className="rounded-2xl border border-border bg-card p-6 shadow-2xl sm:p-8">
                    <div className="mb-5 flex items-center gap-3">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Music className="h-6 w-6" />
                      </span>
                      <p className="font-display text-lg font-semibold">{video.title || "Áudio"}</p>
                    </div>
                    <audio src={video.url} controls data-testid="public-audio-player" className="w-full" />
                  </div>
                ) : mt === "link" ? (
                  <a href={video.url} target="_blank" rel="noreferrer" data-testid="public-link-card"
                    className="group flex items-center gap-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-6 transition-all hover:border-primary/60 hover:shadow-lg">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-105">
                      <ExternalLink className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-semibold leading-snug text-foreground">{video.title || "Abrir link"}</p>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{video.url.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-transform group-hover:scale-105 sm:inline-block">Abrir</span>
                  </a>
                ) : (
                  <div className="relative overflow-hidden rounded-2xl border border-border bg-black shadow-2xl">
                    <div className="aspect-video w-full">
                      {!started ? (
                        <button type="button" data-testid="public-play-overlay" onClick={handleStart} className="group relative h-full w-full" aria-label="Reproduzir vídeo">
                          {poster ? (
                            <img src={poster} alt={video.title || "Capa do vídeo"} className="h-full w-full object-cover" data-testid="public-video-cover" />
                          ) : (
                            <div data-testid="public-video-cover" className="flex h-full w-full items-center justify-center bg-gradient-to-br from-indigo-600 via-slate-900 to-slate-950" />
                          )}
                          <div className="absolute inset-0 bg-black/35 transition-colors group-hover:bg-black/25" />
                          <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 text-slate-900 shadow-2xl transition-transform duration-300 group-hover:scale-110">
                            <Play className="ml-1 h-9 w-9 fill-current" />
                          </span>
                          {video.title && (
                            <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-5 text-left font-display text-lg font-semibold text-white sm:text-xl">
                              {video.title}
                            </span>
                          )}
                        </button>
                      ) : resolved.kind === "iframe" ? (
                        <iframe data-testid="public-video-player" src={autoplaySrc()} title={video.title || "Vídeo"} className="h-full w-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                      ) : (
                        <video data-testid="public-video-player" src={resolved.src} poster={poster || undefined} controls autoPlay className="h-full w-full" />
                      )}
                    </div>
                  </div>
                )}

                {video.title && mt !== "link" && (
                  <h1 data-testid="public-video-title" className="font-display mt-8 text-2xl font-bold tracking-tight sm:text-3xl">{video.title}</h1>
                )}
                {video.description && (
                  <p data-testid="public-video-description" className="mt-3 whitespace-pre-line text-base leading-relaxed text-muted-foreground">{video.description}</p>
                )}
              </>
            ) : (
              <div data-testid="no-video-available-state" className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border bg-card px-6 py-20 text-center">
                <span className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary text-muted-foreground">
                  <VideoOff className="h-8 w-8" />
                </span>
                <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Nenhum conteúdo disponível</h1>
                <p className="mt-3 max-w-md text-base text-muted-foreground">
                  Ainda não há conteúdo publicado nesta página. Assim que for publicado, ele aparecerá aqui automaticamente — sem precisar trocar o QR Code.
                </p>
              </div>
            )}

            {/* ---------- "Amém" prayer button (compact, matches contact cards) ---------- */}
            {c.amen_enabled !== false && (
              !amenDone ? (
                <button
                  type="button"
                  onClick={handleAmen}
                  disabled={amenLoading}
                  data-testid="amen-button"
                  className="group mt-6 flex w-full items-center gap-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-5 text-left transition-all hover:border-primary/60 hover:shadow-lg hover:shadow-primary/10 disabled:opacity-70"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md transition-transform group-hover:scale-105">
                    <HandHeart className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p data-testid="amen-prompt" className="font-display text-base font-semibold leading-snug text-foreground">{c.amen_prompt || "Orou conosco hoje?"}</p>
                    <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.amen_subtitle || "Aperte no botão Amém."}</p>
                  </div>
                  <span className="shrink-0 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-primary-foreground transition-transform group-hover:scale-105">
                    {amenLoading ? "…" : (c.amen_button || "Amém")}
                  </span>
                </button>
              ) : (
                <div data-testid="amen-done-state" className="mt-6 flex w-full items-center gap-4 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 to-primary/5 p-5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                    <Check className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-display text-base font-semibold leading-snug text-foreground">{c.amen_thanks || "Amém! 🙏"}</p>
                    <p className="mt-0.5 text-sm text-muted-foreground">{c.amen_thanks_sub || "Sua oração foi registrada. Que Deus abençoe você."}</p>
                  </div>
                </div>
              )
            )}

            {/* ---------- Contact cards (independent from the video) ---------- */}
            {hasCards && (
              <div className="mt-6 space-y-4" data-testid="public-contact-cards">
                {c.whatsapp_enabled && c.whatsapp_number && (
                  <a
                    href={`https://wa.me/${(c.whatsapp_number || "").replace(/\D/g, "")}${c.whatsapp_message ? `?text=${encodeURIComponent(c.whatsapp_message)}` : ""}`}
                    target="_blank" rel="noreferrer" data-testid="public-whatsapp-card"
                    className="group flex items-center gap-4 rounded-2xl border border-[#25D366]/30 bg-gradient-to-br from-[#25D366]/10 to-emerald-500/5 p-5 transition-all hover:border-[#25D366]/60 hover:shadow-lg hover:shadow-[#25D366]/10"
                  >
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#25D366] text-white shadow-md transition-transform group-hover:scale-105">
                      <MessageCircle className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      {c.whatsapp_message && <p className="font-display text-base font-semibold leading-snug text-foreground">{c.whatsapp_message}</p>}
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">Falar no WhatsApp · {c.whatsapp_number}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-white transition-transform group-hover:scale-105 sm:inline-block">Chamar</span>
                  </a>
                )}

                {c.pix_enabled && c.pix_key && (
                  <div data-testid="public-pix-card" className="rounded-2xl border border-teal-500/30 bg-gradient-to-br from-teal-500/10 to-emerald-500/5 p-5">
                    <div className="flex items-center gap-3">
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-500 text-white shadow-md">
                        <QrCode className="h-5 w-5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base font-semibold leading-snug text-foreground">{c.pix_message || "Chave Pix"}</p>
                        <p className="mt-0.5 truncate font-mono text-sm text-muted-foreground" data-testid="public-pix-key">{c.pix_key}</p>
                      </div>
                    </div>
                    <button type="button" onClick={copyPix} data-testid="public-pix-copy-button"
                      className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-teal-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-teal-600 active:scale-[0.98]">
                      {pixCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                      {pixCopied ? "Chave copiada!" : "Copiar chave Pix"}
                    </button>
                  </div>
                )}

                {c.instagram_enabled && c.instagram_url && (
                  <a href={c.instagram_url} target="_blank" rel="noreferrer" data-testid="public-instagram-card"
                    className="group flex items-center gap-4 rounded-2xl border border-[#E1306C]/30 bg-gradient-to-br from-[#E1306C]/10 via-[#F77737]/5 to-[#833AB4]/10 p-5 transition-all hover:border-[#E1306C]/60 hover:shadow-lg hover:shadow-[#E1306C]/10">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#F77737] via-[#E1306C] to-[#833AB4] text-white shadow-md transition-transform group-hover:scale-105">
                      <Instagram className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-semibold leading-snug text-foreground">Siga no Instagram</p>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.instagram_url.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-[#E1306C] px-4 py-2 text-sm font-semibold text-white transition-transform group-hover:scale-105 sm:inline-block">Seguir</span>
                  </a>
                )}

                {c.facebook_enabled && c.facebook_url && (
                  <a href={c.facebook_url} target="_blank" rel="noreferrer" data-testid="public-facebook-card"
                    className="group flex items-center gap-4 rounded-2xl border border-[#1877F2]/30 bg-gradient-to-br from-[#1877F2]/10 to-blue-500/5 p-5 transition-all hover:border-[#1877F2]/60 hover:shadow-lg hover:shadow-[#1877F2]/10">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#1877F2] text-white shadow-md transition-transform group-hover:scale-105">
                      <Facebook className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-semibold leading-snug text-foreground">Curta no Facebook</p>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.facebook_url.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-[#1877F2] px-4 py-2 text-sm font-semibold text-white transition-transform group-hover:scale-105 sm:inline-block">Abrir</span>
                  </a>
                )}

                {c.youtube_enabled && c.youtube_url && (
                  <a href={c.youtube_url} target="_blank" rel="noreferrer" data-testid="public-youtube-card"
                    className="group flex items-center gap-4 rounded-2xl border border-[#FF0000]/30 bg-gradient-to-br from-[#FF0000]/10 to-red-500/5 p-5 transition-all hover:border-[#FF0000]/60 hover:shadow-lg hover:shadow-[#FF0000]/10">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#FF0000] text-white shadow-md transition-transform group-hover:scale-105">
                      <Youtube className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-semibold leading-snug text-foreground">Inscreva-se no YouTube</p>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.youtube_url.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-[#FF0000] px-4 py-2 text-sm font-semibold text-white transition-transform group-hover:scale-105 sm:inline-block">Abrir</span>
                  </a>
                )}

                {c.tiktok_enabled && c.tiktok_url && (
                  <a href={c.tiktok_url} target="_blank" rel="noreferrer" data-testid="public-tiktok-card"
                    className="group flex items-center gap-4 rounded-2xl border border-foreground/20 bg-gradient-to-br from-foreground/5 to-foreground/10 p-5 transition-all hover:border-foreground/40 hover:shadow-lg">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-foreground text-background shadow-md transition-transform group-hover:scale-105">
                      <TikTokIcon className="h-6 w-6" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-display text-base font-semibold leading-snug text-foreground">Siga no TikTok</p>
                      <p className="mt-0.5 truncate text-sm text-muted-foreground">{c.tiktok_url.replace(/^https?:\/\//, "")}</p>
                    </div>
                    <span className="hidden shrink-0 rounded-full bg-foreground px-4 py-2 text-sm font-semibold text-background transition-transform group-hover:scale-105 sm:inline-block">Abrir</span>
                  </a>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};
